// Words on each photo, placed by rule (owner, 2026-10-06 — #WA-269649).
//
// ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
//
// The client asked for a line in Spanish and one in Arabic on every photo of
// her niece's album. A team member wrote all 46 by hand in 25 minutes: three
// boxes started left of the page (x < 0), five ended right of it, one began
// with the editor's « Votre texte » placeholder. The album agent (worker) and
// the editor's « Légendes IA » (web) now place every caption with THIS file,
// so a caption lands in the same place whoever wrote the words.
//
// ─── THE RULES ──────────────────────────────────────────────────────────────
//
//   · ONE text element per photo, in a band at the top or the bottom of the
//     photo's own slot — never over a face, never over another text, never off
//     the page, never inside the 5 mm print safe zone (hardcover spec) — we
//     keep CAPTION_EDGE_MM from the trim.
//   · `captionFor: <photo id>` — the caption belongs to its PHOTO: running the
//     tool again replaces it, and it follows the photo to another page.
//   · One font, one size for the whole album. The ink is chosen for the part
//     of the photo it sits on, with a thin outline in the opposite colour so it
//     reads on a busy picture. THE PHOTO IS NEVER TINTED: no band, no veil, no
//     box behind the words (owner's rule).
//   · A page we cannot read the geometry of (a decorative template) is not
//     guessed at: it is reported, like a page where every band covers a face.
//
// Coordinates: % of the trim page (0..100), y grows DOWN — the editor's
// PageElement space. The renderers (editor DOM, print raster) wrap the words at
// the element's width and centre them in its box; the box here is sized from
// an estimate of the text width that errs WIDE, so the real text never needs
// more room than the box it was given.
//
// ⚠️ ONE FILE, SHIPPED TWICE: web/src/lib/captionLayout.ts and
// worker/src/lib/captionLayout.ts are byte-identical (captionLayout.shared.test.ts),
// like slotGeometry.ts and bookStructure.ts, the only things it imports.
//
// Pure. Guard: captionLayout.test.ts

import { slotRects, baseFrameMm, BASE_GAP_MM } from './slotGeometry';
import { isThemedBook, spineIndex } from './bookStructure';

/** The interior's finished trim (pdfGenerator PAGE_W / A4_H). */
export const PAGE_W_MM = 200;
export const PAGE_H_MM = 297;
/** Hardcover print safe zone: nothing that must survive the blade within this of the trim. */
export const SAFE_MM = 5;
/** Our captions keep this much from the trim — the safe zone and a little air. */
export const CAPTION_EDGE_MM = 8;
/** From a slot edge that is NOT the trim (the line between two photos). */
export const CAPTION_GUTTER_MM = 4;
/** Air kept around a face. */
export const FACE_PAD_MM = 3;
/** Air kept around another element on the page. */
export const ELEMENT_PAD_MM = 1.5;

/** The editor's page reference width: fontSize is px on a 400-px-wide page (`fontSize/4 cqi`). */
export const EDITOR_REF_W = 400;
/** The editor's and the raster's line height. */
export const LINE_HEIGHT = 1.3;

/** Latin in the brand's display face; Arabic in Cairo (Londrina Solid has no Arabic letters). */
export const CAPTION_FONT_FAMILY = "'Londrina Solid', 'Cairo', sans-serif";
export const CAPTION_FONT_WEIGHT = '700';
/** Font size on full-width photos, and the album's size when any captioned photo is narrow. */
export const CAPTION_SIZE_FULL = 14;
export const CAPTION_SIZE_SMALL = 11;
/** A slot narrower than this (mm) takes the small size — for EVERY caption of the album. */
export const NARROW_SLOT_MM = 120;
/** The outline around each letter, as a fraction of the font size. */
export const CAPTION_OUTLINE = 0.08;
/**
 * THE BOOK-COLOUR BAND (owner, 2026-10-06 — « like the book story of Roaa … a
 * pink frame »). The words sit on a band of the book's own colour (its cover
 * spine), at Roaa's 88 %, in the book's ink, in serif faces that carry Arabic
 * letters (Amiri) and Latin ones (Playfair Display) — readableText.ts's palette.
 * The band covers the photo only behind the words; the rest of the photo is
 * never tinted.
 */
export const BAND_FONT_FAMILY = "'Playfair Display', 'Amiri', serif";
export const BAND_FONT_WEIGHT = '600';
export const BAND_OPACITY = 0.88;
/** Roaa's book: spine pink and plum — the band the owner showed. */
export const BAND_DEFAULT = { band: '#F4C8C8', ink: '#5B2F3F' } as const;
/** Air inside the band around the words, in em of the font size. */
const BAND_PAD_X_EM = 0.8;
const BAND_PAD_Y_EM = 0.9;
/** Roaa's band (x 10→90 %, y 85→94 %): 80 % of its photo wide, its edge ≈ 18 mm from the trim. */
const BAND_WIDTH_FRAC = 0.8;
const BAND_EDGE_MM = 18;
export interface CaptionStyle { band: string; ink: string; opacity?: number }
export const INK_DARK = '#1F1A1C';
export const INK_LIGHT = '#FFFFFF';
/** WCAG crossover: below this relative luminance white reads better than near-black. */
export const LIGHT_INK_BELOW = 0.2;

export interface Box { x: number; y: number; width: number; height: number }
export interface FaceBox { x: number; y: number; width: number; height: number }

export interface SlotPos {
  x?: number; y?: number; scale?: number; rotation?: number; _v?: number;
  crop?: { x: number; y: number; w: number; h: number };
}

/** The element fields this file reads on a page (the editor's PageElement, loosely). */
export interface PageEl {
  id: string; type: string;
  x: number; y: number; width: number; height: number;
  text?: string;
  captionFor?: string;
  [k: string]: unknown;
}

export interface CaptionPage {
  page: number;
  layout: string;
  photos: string[];
  elements?: PageEl[];
  slot_positions?: Record<string, SlotPos | undefined>;
  top_h_frac?: number | null;
  template_id?: string | null;
  blank?: boolean;
  [k: string]: unknown;
}

/** A photo's size and faces, in its own pixels (photos.width/height/face_data). */
export interface CaptionPhoto { id: string; width: number; height: number; faces: FaceBox[] }

export interface CaptionElement extends PageEl {
  type: 'text';
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontColor: string;
  textAlign: 'left' | 'center' | 'right';
  opacity: number;
  outlineColor: string;
  outlineWidth: number;
  captionFor: string;
  /** The slot its photo had when the caption was placed, and the page layout:
   *  the caption follows a photo that MOVES (or a page re-laid out), and stays
   *  wherever a person dragged it otherwise. */
  captionSlot?: number;
  captionLayout?: string;
  /** The book-colour band behind the words (CaptionStyle). Absent = outline style. */
  bandFill?: string;
  bandOpacity?: number;
  /** A model page's caption on the PAPER next to its photo (no holder for it): the model's caption look. */
  captionFree?: true;
  fontStyle?: string;
}

/** Mean relative luminance (0..1, WCAG) of a region, and how busy it is. */
export interface Luma { mean: number; std: number }
/** Region in the photo's OWN normalised coordinates (0..1). Null = could not look. */
export type LumaSampler = (photoId: string, region: { x0: number; y0: number; x1: number; y1: number }) => Luma | null;

export type SkipReason = 'face' | 'text' | 'template' | 'too_long' | 'not_in_album' | 'no_photo_size' | 'themed' | 'no_holder' | 'no_room';

const mmX = (mm: number) => (mm / PAGE_W_MM) * 100;
const mmY = (mm: number) => (mm / PAGE_H_MM) * 100;
const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * The album's gap, as a number of mm (edge-to-edge albums) or with the base
 * layout rules' stamp (migration 234), which adds the page frame and fixes the
 * gap at 4 mm. Every function below takes either — a plain number keeps the
 * old behaviour exactly.
 */
export type GapSpec = number | { mm: number; baseRules?: boolean; themedBook?: boolean } | null | undefined;

/**
 * The gap of an album whose pages are `pages`: a themed book (it opens on its
 * S1 page, bookStructure.ts) puts album page 1 on the RIGHT, so the 15 mm spine
 * margin of every base-rules page flips side. Decided from the pages themselves.
 */
export function gapForBook(gapMm: GapSpec, pages: ReadonlyArray<object>): GapSpec {
  if (typeof gapMm !== 'object' || gapMm === null || !gapMm.baseRules) return gapMm;
  const themed = isThemedBook(pages as ReadonlyArray<{ theme?: unknown; model?: unknown }>);
  return !!gapMm.themedBook === themed ? gapMm : { ...gapMm, themedBook: themed };
}

/** Where any caption may be: the trim, less CAPTION_EDGE_MM on every side. */
export function safeBox(): Box {
  return { x: mmX(CAPTION_EDGE_MM), y: mmY(CAPTION_EDGE_MM), width: 100 - 2 * mmX(CAPTION_EDGE_MM), height: 100 - 2 * mmY(CAPTION_EDGE_MM) };
}

/**
 * A THEMED page (design system v2) has no grid: its photos are elements placed
 * by a model. Its « slots » are those photo elements, in the order of
 * `page.photos` (a photo not drawn as an element has no slot).
 */
function themedSlots(page: Pick<CaptionPage, 'photos' | 'elements'>): Box[] | null {
  const els = page.elements ?? [];
  const boxes = (page.photos ?? []).map((id) => els.find((e) => e && e.type === 'photo' && e.photoId === id));
  if (!boxes.length || boxes.some((e) => !e)) return null;
  return boxes.map((e) => ({ x: Number(e!.x), y: Number(e!.y), width: Number(e!.width), height: Number(e!.height) }));
}
const isThemedPage = (page: Partial<CaptionPage>) => Boolean(page.theme) && Boolean(page.blank);

/** The photo slots of a page in page % (y down), in the order of `page.photos`. */
export function slotBoxes(page: Pick<CaptionPage, 'layout' | 'top_h_frac'> & { page?: number; photos?: string[]; elements?: PageEl[]; theme?: unknown; blank?: boolean }, gapMm: GapSpec = 0): Box[] {
  if (isThemedPage(page as Partial<CaptionPage>)) return themedSlots(page as CaptionPage) ?? [];
  const base = typeof gapMm === 'object' && gapMm !== null && gapMm.baseRules === true;
  const mm = base ? BASE_GAP_MM : typeof gapMm === 'object' && gapMm !== null ? gapMm.mm : (gapMm ?? 0);
  const themedBook = typeof gapMm === 'object' && gapMm !== null && gapMm.themedBook === true;
  const frame = base ? baseFrameMm(spineIndex((Number(page.page) || 1) - 1, themedBook)) : null;
  return slotRects(page.layout, page.top_h_frac ?? null, PAGE_W_MM, PAGE_H_MM, mm, frame).map((r) => ({
    x: (r.x / PAGE_W_MM) * 100,
    y: ((PAGE_H_MM - r.y - r.h) / PAGE_H_MM) * 100,
    width: (r.w / PAGE_W_MM) * 100,
    height: (r.h / PAGE_H_MM) * 100,
  }));
}

/** photos.face_data as this file reads it: pixel boxes, junk dropped. */
export function facesOf(raw: unknown): FaceBox[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((f) => f && typeof f === 'object')
    .map((f) => {
      const o = f as Record<string, unknown>;
      return { x: Number(o.x), y: Number(o.y), width: Number(o.width), height: Number(o.height) };
    })
    .filter((f) => [f.x, f.y, f.width, f.height].every(Number.isFinite) && f.width > 0 && f.height > 0);
}

/** A saved framing in the translate model (the editor's migrateSlotPosition). */
function framing(pos: SlotPos | undefined): { tx: number; ty: number; scale: number; rotation: number } {
  if (!pos) return { tx: 0, ty: 0, scale: 1, rotation: 0 };
  const scale = Number.isFinite(pos.scale) && (pos.scale as number) > 0 ? (pos.scale as number) : 1;
  const rotation = Number.isFinite(pos.rotation) ? ((((pos.rotation as number) % 360) + 360) % 360) : 0;
  if (pos._v === 2) return { tx: Number(pos.x ?? 0) || 0, ty: Number(pos.y ?? 0) || 0, scale, rotation };
  const overflow = Math.max(0, scale - 1);
  return { tx: (50 - Number(pos.x ?? 50)) * overflow, ty: (50 - Number(pos.y ?? 50)) * overflow, scale, rotation };
}

/** The photo's visible size: its crop window, turned a quarter if it is turned a quarter. */
function shownSize(img: { width: number; height: number }, pos: SlotPos | undefined): { w: number; h: number } {
  const c = pos?.crop;
  let w = img.width * (c && c.w > 0 ? c.w : 1);
  let h = img.height * (c && c.h > 0 ? c.h : 1);
  const rot = framing(pos).rotation;
  if (Math.abs(rot - 90) < 1 || Math.abs(rot - 270) < 1) [w, h] = [h, w];
  return { w, h };
}

/**
 * Where the photo is DRAWN, in page %: the cover-fit + translate model every
 * renderer shares (editor pdfMirrorBox, PDF computeTranslateDrawParams),
 * including the solo-landscape fit-width rule (lib/soloFit.ts): a landscape
 * alone on a 1-up page is fitted to the WIDTH, cream above and below.
 */
export function drawnImageBox(slot: Box, img: { width: number; height: number }, pos?: SlotPos, layout = ''): Box {
  const { w, h } = shownSize(img, pos);
  const f = framing(pos);
  const slotWmm = (slot.width / 100) * PAGE_W_MM;
  const slotHmm = (slot.height / 100) * PAGE_H_MM;
  if (w <= 0 || h <= 0 || slotWmm <= 0 || slotHmm <= 0) return { ...slot };
  const fitWidth = layout === '1-up' && img.width > img.height;
  const base = fitWidth ? Math.min(slotWmm / w, slotHmm / h) : Math.max(slotWmm / w, slotHmm / h);
  const sw = w * base * f.scale;
  const sh = h * base * f.scale;
  const applyTX = !fitWidth || sw > slotWmm + 0.01;
  const xMm = (slotWmm - sw) / 2 + (applyTX ? (f.tx / 100) * slotWmm : 0);
  const yMm = (slotHmm - sh) / 2 + (f.ty / 100) * slotHmm;
  return {
    x: slot.x + (xMm / PAGE_W_MM) * 100,
    y: slot.y + (yMm / PAGE_H_MM) * 100,
    width: (sw / PAGE_W_MM) * 100,
    height: (sh / PAGE_H_MM) * 100,
  };
}

/** A point of the photo (its own normalised coords) → the shown (cropped, turned) photo's normalised coords. */
function toShown(s: number, t: number, pos: SlotPos | undefined): [number, number] {
  const c = pos?.crop;
  let u = c && c.w > 0 ? (s - c.x) / c.w : s;
  let v = c && c.h > 0 ? (t - c.y) / c.h : t;
  const rot = framing(pos).rotation;
  if (Math.abs(rot - 90) < 1) [u, v] = [1 - v, u];
  else if (Math.abs(rot - 180) < 1) [u, v] = [1 - u, 1 - v];
  else if (Math.abs(rot - 270) < 1) [u, v] = [v, 1 - u];
  return [u, v];
}

/** The inverse: the shown photo's normalised coords → the photo's own. */
function toSource(u: number, v: number, pos: SlotPos | undefined): [number, number] {
  const rot = framing(pos).rotation;
  let s = u, t = v;
  if (Math.abs(rot - 90) < 1) [s, t] = [v, 1 - u];
  else if (Math.abs(rot - 180) < 1) [s, t] = [1 - u, 1 - v];
  else if (Math.abs(rot - 270) < 1) [s, t] = [1 - v, u];
  const c = pos?.crop;
  return [c && c.w > 0 ? c.x + s * c.w : s, c && c.h > 0 ? c.y + t * c.h : t];
}

const clip = (a: Box, b: Box): Box | null => {
  const x0 = Math.max(a.x, b.x), y0 = Math.max(a.y, b.y);
  const x1 = Math.min(a.x + a.width, b.x + b.width), y1 = Math.min(a.y + a.height, b.y + b.height);
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } : null;
};
const area = (b: Box | null) => (b ? b.width * b.height : 0);
const pad = (b: Box, mm: number): Box => ({ x: b.x - mmX(mm), y: b.y - mmY(mm), width: b.width + 2 * mmX(mm), height: b.height + 2 * mmY(mm) });

/**
 * The faces of the photo in slot `slotIndex`, in page %, clipped to the slot
 * (a face the framing crops away is not on the page). A photo turned by a
 * free angle is approximated by the bounding box of its turned face.
 */
export function facesOnPage(page: CaptionPage, slotIndex: number, photo: CaptionPhoto, gapMm: GapSpec = 0): Box[] {
  const { slot, boxes } = faceBoxes(page, slotIndex, photo, gapMm);
  if (!slot) return [];
  const out: Box[] = [];
  for (const box of boxes) {
    const seen = clip(box, slot);
    if (seen) out.push(seen);
  }
  return out;
}

/**
 * How much of each face the framing shows, 0..1 (the part inside the slot over
 * the whole face) — the preview quality check's « no cut faces » (2026-10-08).
 */
export function faceVisibility(page: CaptionPage, slotIndex: number, photo: CaptionPhoto, gapMm: GapSpec = 0): number[] {
  const { slot, boxes } = faceBoxes(page, slotIndex, photo, gapMm);
  if (!slot) return [];
  return boxes.map((b) => (area(b) > 0 ? area(clip(b, slot)) / area(b) : 1));
}

/** The framing of slot `slotIndex`: the saved slot position, or a themed photo element's crop. */
function slotPosOf(page: CaptionPage, slotIndex: number): SlotPos | undefined {
  if (isThemedPage(page)) {
    const id = (page.photos ?? [])[slotIndex];
    const e = (page.elements ?? []).find((x) => x && x.type === 'photo' && x.photoId === id);
    const crop = e?.crop as SlotPos['crop'] | undefined;
    return crop ? { crop, _v: 2, x: 0, y: 0, scale: 1 } : undefined;
  }
  return page.slot_positions?.[`s${slotIndex}`];
}

/** Every face of the photo in slot `slotIndex`, in page %, NOT clipped. */
function faceBoxes(page: CaptionPage, slotIndex: number, photo: CaptionPhoto, gapMm: GapSpec): { slot: Box | null; boxes: Box[] } {
  const slot = slotBoxes(page, gapMm)[slotIndex];
  if (!slot || !(photo.width > 0) || !(photo.height > 0)) return { slot: null, boxes: [] };
  const pos = slotPosOf(page, slotIndex);
  const drawn = drawnImageBox(slot, photo, pos, isThemedPage(page) ? '' : page.layout);
  const rot = framing(pos).rotation;
  const quarter = [0, 90, 180, 270].some((q) => Math.abs(rot - q) < 1);
  const out: Box[] = [];
  for (const f of photo.faces ?? []) {
    if (!(f && f.width > 0 && f.height > 0)) continue;
    const s0 = Math.max(0, f.x / photo.width), t0 = Math.max(0, f.y / photo.height);
    const s1 = Math.min(1, (f.x + f.width) / photo.width), t1 = Math.min(1, (f.y + f.height) / photo.height);
    const corners = [[s0, t0], [s1, t0], [s0, t1], [s1, t1]].map(([s, t]) => toShown(s, t, pos));
    let pts = corners.map(([u, v]) => [drawn.x + u * drawn.width, drawn.y + v * drawn.height]);
    if (!quarter) {
      // A free angle turns the photo about the centre of its drawn box.
      const cx = drawn.x + drawn.width / 2, cy = drawn.y + drawn.height / 2;
      const a = (rot * Math.PI) / 180;
      pts = pts.map(([px, py]) => {
        const dx = ((px - cx) / 100) * PAGE_W_MM, dy = ((py - cy) / 100) * PAGE_H_MM;
        return [cx + ((dx * Math.cos(a) - dy * Math.sin(a)) / PAGE_W_MM) * 100, cy + ((dx * Math.sin(a) + dy * Math.cos(a)) / PAGE_H_MM) * 100];
      });
    }
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
    out.push({ x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) });
  }
  return { slot, boxes: out };
}

// ─── THE WORDS, MEASURED WITHOUT A BROWSER ──────────────────────────────────
//
// The worker has no canvas. Each character gets an advance in ems that errs
// WIDE of Londrina Solid (Latin) and Cairo (Arabic) at the caption weight, so
// the box is never narrower than the real line. Calibrated in Chrome
// (captionLayout.test.ts « the width estimate against the real fonts »): Cairo's
// س ش ص ض are almost a full em; its other letters about two thirds.

const ARABIC_MARK = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/;
const ARABIC_WIDE = /[\u0633\u0634\u0635\u0636]/;
const ARABIC_LETTER = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * `face` 'serif' = the band faces: Amiri's Arabic is about three quarters of
 * Cairo's width (≤ 0.78 measured), Playfair's Latin a quarter wider than
 * Londrina's and its capitals almost a full em (measured in Chrome, see the test).
 */
export function lineWidthEm(line: string, face: 'display' | 'serif' = 'display'): number {
  if (face === 'serif') return serifWidthEm(line);
  let em = 0;
  for (const ch of String(line ?? '')) {
    if (/\s/.test(ch)) em += 0.26;
    else if (ARABIC_MARK.test(ch)) em += 0;
    else if (ch === '\u060C' || ch === '\u061B' || ch === '\u061F') em += 0.3;
    else if (ARABIC_WIDE.test(ch)) em += 1.05;
    else if (ARABIC_LETTER.test(ch)) em += 0.66;
    else if (/[A-Z0-9ÀÁÂÄÈÉÊËÌÍÎÏÒÓÔÖÙÚÛÜÑÇ]/.test(ch)) em += 0.58;
    else if (/[.,;:!¡¿?'\u2019"«»()\-\u2013\u2014/]/.test(ch)) em += 0.3;
    else em += 0.5;
  }
  return em;
}

/** How many rendered lines one written line needs in a box `widthMm` wide. */
function serifWidthEm(line: string): number {
  let em = 0;
  for (const ch of String(line ?? '')) {
    if (/\s/.test(ch)) em += 0.26;
    else if (ARABIC_MARK.test(ch)) em += 0;
    else if (ch === '\u060C' || ch === '\u061B' || ch === '\u061F') em += 0.24;
    else if (ARABIC_WIDE.test(ch)) em += 0.84;
    else if (ARABIC_LETTER.test(ch)) em += 0.53;
    else if (/[A-Z0-9ÀÁÂÄÈÉÊËÌÍÎÏÒÓÔÖÙÚÛÜÑÇ]/.test(ch)) em += 1.0;
    else if (/[.,;:!¡¿?'\u2019"«»()\-\u2013\u2014/]/.test(ch)) em += 0.32;
    else em += 0.62;
  }
  return em;
}

export function estimateLines(line: string, fontMm: number, widthMm: number, face: 'display' | 'serif' = 'display'): number {
  const need = lineWidthEm(line, face) * fontMm * 1.08;
  return Math.max(1, Math.ceil(need / Math.max(1, widthMm)));
}

const linesOf = (text: string) => String(text ?? '').replace(/\n+$/, '').split('\n');

/** Dark words on a light area, light words on a dark one; the outline is the other. */
export function inkFor(luma: Luma | null): { ink: string; outline: string } {
  if (luma && luma.mean < LIGHT_INK_BELOW) return { ink: INK_LIGHT, outline: INK_DARK };
  return { ink: INK_DARK, outline: INK_LIGHT };
}

/** The part of a slot a caption may use (`safe`: the page's own safe zone; default CAPTION_EDGE_MM all round). */
function usableIn(slot: Box, safe: Box = safeBox()): Box | null {
  let u = clip(slot, safe);
  if (!u) return null;
  const onTrim = (v: number, edge: number) => Math.abs(v - edge) < 0.01;
  const left = onTrim(slot.x, 0) ? 0 : mmX(CAPTION_GUTTER_MM);
  const right = onTrim(slot.x + slot.width, 100) ? 0 : mmX(CAPTION_GUTTER_MM);
  const top = onTrim(slot.y, 0) ? 0 : mmY(CAPTION_GUTTER_MM);
  const bottom = onTrim(slot.y + slot.height, 100) ? 0 : mmY(CAPTION_GUTTER_MM);
  u = { x: u.x + left, y: u.y + top, width: u.width - left - right, height: u.height - top - bottom };
  return u.width > 0 && u.height > 0 ? u : null;
}

export type PlaceResult =
  | { ok: true; element: CaptionElement; band: 'top' | 'bottom'; coversFace: false }
  | { ok: false; reason: SkipReason; why: string };

export interface PlaceOpts {
  fontSize: number;
  gapMm?: GapSpec;
  luma?: LumaSampler;
  id?: string;
  /** Text elements that do not block (the hand-written ones the person asked to replace). */
  ignore?: (el: PageEl) => boolean;
  /** The book-colour band; absent = words with an outline, no band. */
  style?: CaptionStyle | null;
  /** The person's own font / ink for this run (« Police », « Couleur »); absent = the caption's own. */
  fontFamily?: string | null;
  fontColor?: string | null;
  /** The page's safe zone (a model page: pageSafeBox — 12 mm, 15 mm on the spine side). Default safeBox(). */
  safe?: Box;
}

/** A light ink gets a dark outline, a dark ink a light one (the person's own colour, read on any photo). */
export function outlineFor(ink: string): string {
  return HEX6.test(ink) && relLum(ink) < 0.4 ? INK_LIGHT : INK_DARK;
}

/** Place ONE caption for `photoId` on `page`. Never throws. */
export function placeCaption(page: CaptionPage, photoId: string, text: string, photo: CaptionPhoto, opts: PlaceOpts): PlaceResult {
  const slotIndex = (page.photos ?? []).indexOf(photoId);
  if (slotIndex < 0) return { ok: false, reason: 'not_in_album', why: 'the photo is not on this page' };
  if (page.template_id) return { ok: false, reason: 'template', why: 'a decorative template page — its frames are not the standard grid; place this one by hand' };
  const gapMm = opts.gapMm ?? 0;
  const slot = slotBoxes(page, gapMm)[slotIndex];
  if (!slot && isThemedPage(page)) return { ok: false, reason: 'themed', why: 'a themed page whose photo is not a layer of its own — place this one by hand' };
  const usable = slot ? usableIn(slot, opts.safe) : null;
  if (!slot || !usable) return { ok: false, reason: 'too_long', why: 'the photo has no room for words inside the print safe zone' };

  const fontMm = (opts.fontSize * PAGE_W_MM) / EDITOR_REF_W;
  const lines = linesOf(text).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { ok: false, reason: 'too_long', why: 'there are no words to write' };
  const usableWmm = (usable.width / 100) * PAGE_W_MM;
  const style = opts.style ?? null;
  // A face of the person's choice is measured with the wider estimate (errs wide).
  const ownFont = String(opts.fontFamily ?? '').trim();
  const face = style || ownFont ? 'serif' : 'display';
  const extraMm = style ? fontMm * 2 * BAND_PAD_X_EM : fontMm * (2 * CAPTION_OUTLINE + 0.5);
  const widest = Math.max(...lines.map((l) => lineWidthEm(l, face) * fontMm * 1.08));
  const slotWmm = (slot.width / 100) * PAGE_W_MM;
  const boxWmm = style
    ? Math.min(usableWmm, Math.max(widest + extraMm, slotWmm * BAND_WIDTH_FRAC))
    : Math.min(usableWmm, widest + extraMm);
  const rows = lines.reduce((n, l) => n + estimateLines(l, fontMm, boxWmm - (style ? extraMm : extraMm / 2), face), 0);
  const boxHmm = rows * fontMm * LINE_HEIGHT + (style ? fontMm * 2 * BAND_PAD_Y_EM : fontMm * 0.35);
  const boxW = mmX(boxWmm), boxH = mmY(boxHmm);
  if (boxH > usable.height / 2) return { ok: false, reason: 'too_long', why: 'the words need more than half of the photo — shorten them' };

  const faces = (photo.width > 0 && photo.height > 0 ? facesOnPage(page, slotIndex, photo, gapMm) : []).map((f) => pad(f, FACE_PAD_MM));
  // On a themed page the photo's own element, its frame (same group) and the
  // paper layers under it are not obstacles: the words sit on the photo, as
  // on a grid page. An EMPTY holder is not printed, so it is not one either.
  const own = isThemedPage(page) ? (page.elements ?? []).find((e) => e && e.type === 'photo' && e.photoId === photoId) : undefined;
  const others = (page.elements ?? [])
    .filter((e) => e && ['text', 'photo', 'icon'].includes(String(e.type)) && e.captionFor !== photoId && !(opts.ignore?.(e)))
    .filter((e) => !own || (e !== own && !(own.group && e.group === own.group) && e.layer !== 'paper'
      && !(e.type === 'text' && e.holder && !String(e.text ?? '').trim())))
    .map((e) => pad({ x: Number(e.x), y: Number(e.y), width: Number(e.width), height: Number(e.height) }, ELEMENT_PAD_MM));

  const pos = slotPosOf(page, slotIndex);
  const drawn = photo.width > 0 && photo.height > 0 ? drawnImageBox(slot, photo, pos, isThemedPage(page) ? '' : page.layout) : slot;
  const lumaOf = (b: Box): Luma | null => {
    if (!opts.luma) return null;
    const onPhoto = clip(b, drawn);
    // Entirely on the page's own paper (a letterboxed landscape): the paper is light.
    if (!onPhoto) return { mean: 0.85, std: 0 };
    const [s0, t0] = toSource((onPhoto.x - drawn.x) / drawn.width, (onPhoto.y - drawn.y) / drawn.height, pos);
    const [s1, t1] = toSource((onPhoto.x + onPhoto.width - drawn.x) / drawn.width, (onPhoto.y + onPhoto.height - drawn.y) / drawn.height, pos);
    const region = { x0: Math.max(0, Math.min(s0, s1)), y0: Math.max(0, Math.min(t0, t1)), x1: Math.min(1, Math.max(s0, s1)), y1: Math.min(1, Math.max(t0, t1)) };
    try { return opts.luma(photoId, region); } catch { return null; }
  };

  // The band keeps Roaa's air from the trim (BAND_EDGE_MM), never past the usable box.
  const onTrimTop = Math.abs(slot.y) < 0.01, onTrimBottom = Math.abs(slot.y + slot.height - 100) < 0.01;
  const lift = style ? mmY(BAND_EDGE_MM - CAPTION_EDGE_MM) : 0;
  const topY = usable.y + (style && onTrimTop ? lift : 0);
  const bottomY = usable.y + usable.height - boxH - (style && onTrimBottom ? lift : 0);
  type Cand = { band: 'top' | 'bottom'; align: number; box: Box; face: number; text: boolean };
  const cands: Cand[] = [];
  for (const band of ['bottom', 'top'] as const) {
    const y = band === 'top' ? Math.min(topY, usable.y + usable.height - boxH) : Math.max(usable.y, bottomY);
    const xs = [usable.x + (usable.width - boxW) / 2, usable.x, usable.x + usable.width - boxW];
    xs.forEach((x, align) => {
      const box = { x, y, width: boxW, height: boxH };
      cands.push({
        band, align, box,
        face: faces.reduce((n, f) => n + area(clip(box, f)), 0),
        text: others.some((o) => area(clip(box, o)) > 0),
      });
    });
  }
  const free = cands.filter((c) => c.face === 0 && !c.text);
  if (!free.length) {
    const textOnly = cands.some((c) => c.face === 0);
    return textOnly
      ? { ok: false, reason: 'text', why: 'another text already sits in both bands of this photo' }
      : { ok: false, reason: 'face', why: 'every place for the words would cover a face' };
  }
  // Centred before the sides; then the calmer band; then the bottom.
  const bestAlign = Math.min(...free.map((c) => c.align));
  const pool = free.filter((c) => c.align === bestAlign);
  let pick = pool.find((c) => c.band === 'bottom') ?? pool[0];
  const other = pool.find((c) => c !== pick);
  const lumas = new Map(pool.map((c) => [c, lumaOf(c.box)]));
  if (other) {
    const a = lumas.get(pick), b = lumas.get(other);
    if (a && b && b.std + 0.03 < a.std) pick = other;
  }
  const read = inkFor(lumas.get(pick) ?? null);
  const ownInk = String(opts.fontColor ?? '').trim();
  const ink = ownInk || read.ink;
  const outline = ownInk ? outlineFor(ownInk) : read.outline;
  const base = {
    id: opts.id ?? `caption-${photoId}`,
    type: 'text' as const,
    x: r2(pick.box.x), y: r2(pick.box.y), width: r2(pick.box.width), height: r2(pick.box.height),
    text: lines.join('\n'),
    fontSize: opts.fontSize,
    textAlign: 'center' as const,
    opacity: 1,
    captionFor: photoId,
    captionSlot: slotIndex,
    captionLayout: page.layout,
  };
  return {
    ok: true, band: pick.band, coversFace: false,
    element: style
      ? {
        ...base,
        fontFamily: ownFont || BAND_FONT_FAMILY, fontWeight: ownFont ? '400' : BAND_FONT_WEIGHT, fontColor: ownInk || style.ink,
        outlineColor: ownInk || style.ink, outlineWidth: 0,
        bandFill: style.band, bandOpacity: style.opacity ?? BAND_OPACITY,
      }
      : {
        ...base,
        fontFamily: ownFont || CAPTION_FONT_FAMILY, fontWeight: ownFont ? '400' : CAPTION_FONT_WEIGHT, fontColor: ink,
        outlineColor: outline, outlineWidth: CAPTION_OUTLINE,
      },
  };
}

/** Where a photo is in the album: its page (by index) and slot. */
function locate(pages: CaptionPage[], photoId: string): { pi: number; si: number } | null {
  for (let pi = 0; pi < pages.length; pi += 1) {
    const si = (pages[pi].photos ?? []).indexOf(photoId);
    if (si >= 0) return { pi, si };
  }
  return null;
}

// A theme's caption HOLDER is not a free caption: it never moves, is never
// removed, and its size is the theme's (DESIGN SYSTEM STEP 3 — see captionAlbum).
const isCaption = (e: PageEl | undefined | null): e is CaptionElement => Boolean(e && e.type === 'text' && typeof e.captionFor === 'string' && e.captionFor && !e.holder);

/**
 * The album's caption size: the one its captions already use (a re-run on a
 * few pages stays consistent), else the small size when any photo to caption
 * sits in a narrow slot, else the full size.
 */
export function captionFontSize(pages: CaptionPage[], photoIds: string[], gapMm: GapSpec = 0): number {
  for (const p of pages) for (const e of p.elements ?? []) if (isCaption(e) && !e.captionFree && Number(e.fontSize) > 0) return Number(e.fontSize);
  let narrow = false;
  for (const id of photoIds) {
    const at = locate(pages, id);
    if (!at) continue;
    const slot = slotBoxes(pages[at.pi], gapMm)[at.si];
    if (slot && (slot.width / 100) * PAGE_W_MM < NARROW_SLOT_MM) narrow = true;
  }
  return narrow ? CAPTION_SIZE_SMALL : CAPTION_SIZE_FULL;
}

// ─── CAPTION HOLDERS (design system v2 §4) ─────────────────────────────────
//
// CAPTIONS × MODELS (owner, 2026-10-14 — « match the models we have … use the
// text places the models have, so it looks pretty, not random text »). On a
// themed page a photo's words go INTO the model's own text holder for that
// photo — its box, font, size range, colour, alignment and tilt as the model
// drew them. The model is asked to write to the holder's CAPACITY
// (holderCapacity → the writer's per-photo length), so nothing is ever cut:
// the words shrink between the role's sizes, one row per language (a single
// language: up to two rows) — or, in a box one row high with several
// languages, one row « mot · كلمة ». A photo the model gave no holder gets a
// FREE caption on the paper next to it (FREE CAPTIONS ON MODEL PAGES below;
// `themedFree: false` = nothing).

const lineList = (text: string) => String(text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);

/** The roles that take a photo's caption. */
const PHOTO_ROLES = new Set(['legende', 'mot_photo', 'mot_manuscrit']);
/**
 * The PAGE's own boxes a photo's caption may take when the photo has none
 * (owner, 2026-10-17 — #1248: a naissance page's empty Sous-titre / Section
 * stayed empty while the caption went ON the photo in Londrina). After the
 * photo's own boxes and the page's Légende; one photo per box, in page order.
 * Words in them (the theme's or a person's) are replaced only with
 * « Remplacer les textes », and come back with « Retirer » (captionOrigText).
 */
const PAGE_BOX_ROLES = new Set(['sous_titre', 'section']);
/** One printed line, the writer's own ceiling (captionWriter MAX_LINE_CHARS). */
export const MAX_HOLDER_LINE = 56;
/** Fewer characters a line than this is not a caption: the holder is not offered. */
export const MIN_HOLDER_CHARS = 4;
/** Several languages on ONE row (a holder one row high). */
export const INLINE_SEP = ' · ';

/** A holder as this file reads it. */
export type HolderLike = Pick<PageEl, 'width' | 'height'> & {
  fontSize?: unknown; fontSizeMax?: unknown; fontSizeMin?: unknown; fontFamily?: unknown; maxChars?: unknown;
};

/** Average advance of a Latin letter in a font, in em — errs wide. */
function fontK(family: string): number {
  return /Special Elite/.test(family) ? 0.62 : /Amatic/.test(family) ? 0.4 : /Caveat|Indie Flower|Shadows/.test(family) ? 0.46
    : /Fraunces|Playfair|Abril/.test(family) ? 0.58
    // The editor's other faces (« Police » of the dialog), measured wide.
    : /Bungee|Gasoek/.test(family) ? 0.78
    : /Montserrat|Poppins|Permanent Marker|Ruda|Fredoka|Pacifico/.test(family) ? 0.64
    : /Roboto Slab|Inter|DM Sans|Quicksand|Lobster/.test(family) ? 0.6
    : /Bebas|Oswald|Anton/.test(family) ? 0.45
    : 0.55;
}

/** Average advance of a holder font over a line, in em — errs wide (Arabic letters wider). */
function holderEm(line: string, family: string): number {
  const k = fontK(family);
  let em = 0;
  for (const ch of line) em += /\s/.test(ch) ? 0.26 : ARABIC_MARK.test(ch) ? 0 : ARABIC_WIDE.test(ch) ? 1.05 : ARABIC_LETTER.test(ch) ? 0.66 : k;
  return em;
}

/**
 * The size (element units: fontSize / 4 cqi, 2 units = 1 mm) at which `text`
 * fits a holder: from the role's largest down to its smallest, each written
 * line wrapping on its own, in at most `maxRows` rows. Null = does not fit
 * even at the smallest.
 */
export function holderFontSize(text: string, el: HolderLike, maxRows = Number.POSITIVE_INFINITY): number | null {
  const wMm = (Number(el.width) / 100) * PAGE_W_MM, hMm = (Number(el.height) / 100) * PAGE_H_MM;
  const max = Number(el.fontSizeMax ?? el.fontSize) || 0, min = Math.min(max, Number(el.fontSizeMin ?? el.fontSize) || max);
  if (!(max > 0) || !(wMm > 0) || !(hMm > 0)) return null;
  const family = String(el.fontFamily ?? '');
  const lines = lineList(text);
  const step = Math.max(0.1, (max - min) / 8);
  for (let f = max; f >= min - 1e-9; f -= step) {
    const mm = f / 2;
    const rows = lines.reduce((n, l) => n + Math.max(1, Math.ceil((holderEm(l, family) * mm * 1.05) / (wMm * 0.92))), 0);
    if (rows <= maxRows && rows * LINE_HEIGHT * mm <= hMm + 1e-6) return Math.round(f * 100) / 100;
  }
  return null;
}

export interface HolderFit { text: string; fontSize: number; mode: 'stack' | 'inline' }

/**
 * A caption (one written line per language) laid into a holder: one row per
 * language — a single language may wrap to two rows — at the largest size of
 * the role that holds them; else several languages on ONE row « mot · كلمة »
 * (a polaroid chin is one row high). A role's own length (Mot sous photo
 * ≤ 12) holds for each language. Null = the words do not fit even at the
 * role's smallest size: never cut, never past the box — shorter words.
 */
export function fitHolder(text: string, el: HolderLike): HolderFit | null {
  const lines = lineList(text);
  if (!lines.length) return null;
  const roleMax = Number(el.maxChars) || 0;
  if (roleMax > 0 && lines.some((l) => l.length > roleMax)) return null;
  const stack = lines.join('\n');
  const s = holderFontSize(stack, el, lines.length === 1 ? 2 : lines.length);
  if (s != null) return { text: stack, fontSize: s, mode: 'stack' };
  if (lines.length > 1) {
    const one = lines.join(INLINE_SEP);
    const i = holderFontSize(one, el, 1);
    if (i != null) return { text: one, fontSize: i, mode: 'inline' };
  }
  return null;
}

/**
 * How many characters each language line may have in this holder — the length
 * the model is asked to write to, so its words fit without being cut. Measured
 * at a size halfway between the largest that holds the rows and the role's
 * smallest (the words do not end up at the floor). `langs`: the language codes
 * ('ar' = Arabic letters, wider). Null = the holder cannot take that many
 * languages.
 */
export function holderCapacity(el: HolderLike, langs: ReadonlyArray<string>): { mode: 'stack' | 'inline'; maxChars: number } | null {
  const n = Math.max(1, langs.length);
  const wMm = (Number(el.width) / 100) * PAGE_W_MM, hMm = (Number(el.height) / 100) * PAGE_H_MM;
  const max = Number(el.fontSizeMax ?? el.fontSize) || 0, min = Math.min(max, Number(el.fontSizeMin ?? el.fontSize) || max);
  if (!(max > 0) || !(wMm > 0) || !(hMm > 0)) return null;
  const family = String(el.fontFamily ?? '');
  // Each language at its own width: Arabic letters (Cairo / Amiri) ≈ 0.7 em, Latin ones the font's.
  const ks = Array.from({ length: n }, (_, i) => (langs[i] === 'ar' ? Math.max(0.7, fontK(family)) : fontK(family)));
  const rowEm = (mm: number) => (wMm * 0.92) / (mm * 1.05);
  const roleMax = Number(el.maxChars) || MAX_HOLDER_LINE;
  const cap = (c: number) => Math.min(Math.floor(c), roleMax, MAX_HOLDER_LINE);
  const maxMm = max / 2, minMm = min / 2;
  const stackMm = Math.min(maxMm, hMm / (n * LINE_HEIGHT));
  if (stackMm >= minMm - 1e-9) {
    const at = (stackMm + minMm) / 2;
    let c = rowEm(at) / Math.max(...ks);                                               // one row per language: the widest sets it
    if (n === 1 && 2 * LINE_HEIGHT * at <= hMm + 1e-6) c *= 1.8;                       // one language may wrap to two rows
    const got = cap(c);
    if (got >= MIN_HOLDER_CHARS) return { mode: 'stack', maxChars: got };
  }
  if (n > 1) {
    const oneMm = Math.min(maxMm, hMm / LINE_HEIGHT);
    if (oneMm >= minMm - 1e-9) {
      const at = (oneMm + minMm) / 2;
      const sepEm = holderEm(INLINE_SEP, family);
      const got = cap((rowEm(at) - sepEm * (n - 1)) / ks.reduce((a, b) => a + b, 0));    // all on one row
      if (got >= MIN_HOLDER_CHARS) return { mode: 'inline', maxChars: got };
    }
  }
  return null;
}

const ROLE_FR: Record<string, string> = { legende: 'Légende', mot_photo: 'Mot sous photo', mot_manuscrit: 'Mot manuscrit', lieu_date: 'Lieu · date', sous_titre: 'Sous-titre', section: 'Section' };

/** The person's font / ink on a holder (none → the model's own). */
function styledHolder(e: PageEl, fontFamily?: string | null, fontColor?: string | null): PageEl {
  const f = String(fontFamily ?? '').trim(), c = String(fontColor ?? '').trim();
  return { ...e, ...(f ? { fontFamily: f, fontWeight: '400', fontStyle: 'normal' } : {}), ...(c ? { fontColor: c } : {}) };
}

/** The model's own look of a holder, kept the first time words are written in it (« Retirer » puts it back). */
type HolderOrig = { fontFamily?: unknown; fontColor?: unknown; fontSize?: unknown; fontWeight?: unknown; fontStyle?: unknown };
const ORIG_KEYS = ['fontFamily', 'fontColor', 'fontSize', 'fontWeight', 'fontStyle'] as const;
/** (null = the holder had no such field: it survives a save, where `undefined` would not.) */
const origOf = (e: PageEl): HolderOrig => (e.captionOrig as HolderOrig | undefined)
  ?? Object.fromEntries(ORIG_KEYS.map((k) => [k, e[k] ?? null]));

/** A holder this tool wrote in, back to empty with the model's own look. */
function emptied(e: PageEl): PageEl {
  const o = (e.captionOrig ?? null) as HolderOrig | null;
  const { captionFor: _f, captionOrig: _o, captionPlace: _p, ...rest } = e;
  void _f; void _o; void _p;
  const { captionOrigText: was, ...kept } = rest as PageEl;
  // The words it held before (a Sous-titre the theme wrote) come back.
  const back: Record<string, unknown> = { ...kept, text: String(was ?? '') };
  if (o) {
    for (const k of ORIG_KEYS) {
      if (!(k in o)) continue;
      if (o[k] === null || o[k] === undefined) delete back[k];
      else back[k] = o[k];
    }
  }
  return back as PageEl;
}

/** A holder whose words this tool wrote (a photo's caption, or the page's « Lieu · date »). */
const isToolHolder = (e: PageEl | null | undefined): boolean => Boolean(e && e.type === 'text' && e.holder && (e.captionFor || e.captionPlace));

/** Words a PERSON (or the theme, from the order) wrote in a holder. */
const handWords = (e: PageEl): boolean => !!String(e.text ?? '').trim() && !isToolHolder(e);

/**
 * The holders of a themed page that may take `photoId`'s words, best first:
 * its own Légende, its own Mot manuscrit, its own Mot sous photo, then the
 * page's Légende that belongs to no photo, then the page's Sous-titre and
 * Section (PAGE_BOX_ROLES).
 */
function holderCands(page: CaptionPage, photoId: string, taken: (e: PageEl) => boolean): Array<{ e: PageEl; index: number }> {
  const pageBox = (e: PageEl) => PAGE_BOX_ROLES.has(String(e.holder));
  return (page.elements ?? []).map((e, index) => ({ e, index }))
    .filter(({ e }) => e && e.type === 'text' && (PHOTO_ROLES.has(String(e.holder)) || (pageBox(e) && !e.holderFor && e.captionPageOf == null)))
    .filter(({ e }) => e.holderFor === photoId || (e.holder === 'legende' && !e.holderFor) || pageBox(e))
    .filter(({ e }) => !taken(e))
    .sort((a, b) => Number(pageBox(a.e)) - Number(pageBox(b.e))
      || Number(b.e.holderFor === photoId) - Number(a.e.holderFor === photoId)
      || Number(a.e.holder === 'mot_photo') - Number(b.e.holder === 'mot_photo')
      || (pageBox(a.e) ? Number(a.e.holder === 'section') - Number(b.e.holder === 'section') : 0));
}

/** Words in the page's Sous-titre / Section do not stop the photo's caption going elsewhere. */
const pageBoxRole = (e: PageEl) => PAGE_BOX_ROLES.has(String(e.holder));

export type HolderPick =
  | { kind: 'holder'; index: number; fontSize: number; text: string; mode: 'stack' | 'inline' }
  | { kind: 'skip'; reason: SkipReason; why: string }
  | { kind: 'none' };

/** The language codes a caption's lines are in, as holderCapacity reads them. */
const scriptsOf = (lines: string[]) => lines.map((l) => (ARABIC_LETTER.test(l) ? 'ar' : 'latin'));

/**
 * Which caption holder of a themed page takes this photo's words (holderCands
 * order). A holder another photo's caption fills is not offered; one a PERSON
 * filled is left alone unless « Remplacer les textes » is on. `none` = the
 * page has no holder for it. `fontFamily`: the person's font, measured instead
 * of the model's.
 */
export function captionHolderFor(page: CaptionPage, photoId: string, text: string, replaceHandText: boolean, fontFamily?: string | null): HolderPick {
  const lines = lineList(text);
  const cands = holderCands(page, photoId, (e) => Boolean(e.captionFor) && e.captionFor !== photoId);
  if (!cands.length) return { kind: 'none' };
  let handFilled = false;
  let tooLong: PageEl | null = null;
  for (const { e, index } of cands) {
    if (handWords(e) && !replaceHandText) { if (!pageBoxRole(e)) handFilled = true; continue; }
    const fit = fitHolder(lines.join('\n'), styledHolder(e, fontFamily));
    if (!fit) { tooLong = tooLong ?? e; continue; }
    return { kind: 'holder', index, fontSize: fit.fontSize, text: fit.text, mode: fit.mode };
  }
  if (handFilled && !tooLong) return { kind: 'skip', reason: 'text', why: 'its caption box already holds words a person wrote — « Remplacer les textes » is off, so they are kept' };
  if (tooLong) {
    const cap = holderCapacity(styledHolder(tooLong, fontFamily), scriptsOf(lines));
    return {
      kind: 'skip', reason: 'too_long',
      why: `the words do not fit the « ${ROLE_FR[String(tooLong.holder)] ?? 'caption'} » box of this model${cap ? ` (≈ ${cap.maxChars} characters a line)` : ''} — shorten them`,
    };
  }
  return { kind: 'skip', reason: 'too_long', why: 'no caption box for this photo' };
}

/** Where a photo's words will go — decided BEFORE they are written, so the model writes words that fit. */
export type CaptionTarget =
  | { kind: 'holder'; page: number; elementId: string; role: string; mode: 'stack' | 'inline'; maxChars: number }
  /** `where` (model pages): 'paper' = a free caption next to the photo; 'photo' = on the photo (no room on the paper). */
  | { kind: 'free'; page: number; maxChars: number; where?: 'paper' | 'photo'; spot?: FreeSpot }
  | { kind: 'none'; page: number | null; reason: SkipReason; why: string };

export interface TargetOpts {
  replaceHandText?: boolean;
  /** A model page's photo with no holder of its own gets a free caption next to it (on it when no room). Default true. */
  themedFree?: boolean;
  /** The person's font — the capacity is measured in it. */
  fontFamily?: string | null;
}

/**
 * Where each photo's caption goes, for these languages: the same choice
 * captionAlbum makes — its own holder, then the page's Légende that belongs to
 * no photo (the first photo of the page without a holder takes it), then
 * (unless `themedFree` is false) a free caption on the paper next to the photo,
 * else on the photo inside the page's safe zone, else nothing (« no_room »).
 * A grid page: on the photo (one line ≤ 56). `maxChars` is the length the
 * model is asked to keep each language line to.
 */
export function captionTargets(pages: CaptionPage[], photoIds: string[], langs: ReadonlyArray<string>, opts: TargetOpts = {}): Record<string, CaptionTarget> {
  const ids = new Set(photoIds);
  const claimed = new Set<string>();
  const out: Record<string, CaptionTarget> = {};
  const themedBook = isThemedBook(pages as ReadonlyArray<{ theme?: unknown; model?: unknown }>);
  const freeSize = freeCaptionSize(pages);
  // Free captions planned so far on each page (a later photo's words do not take the same spot).
  const planned = new Map<number, PageEl[]>();
  const ordered = [...ids].sort((a, b) => {
    const la = locate(pages, a), lb = locate(pages, b);
    return (la ? la.pi * 10 + la.si : 1e9) - (lb ? lb.pi * 10 + lb.si : 1e9);
  });
  for (const id of ordered) {
    const at = locate(pages, id);
    if (!at) { out[id] = { kind: 'none', page: null, reason: 'not_in_album', why: 'this photo is not in the album' }; continue; }
    const pg = pages[at.pi];
    const pageNo = Number(pg.page);
    if (!pg.theme) {
      out[id] = pg.template_id
        ? { kind: 'none', page: pageNo, reason: 'template', why: 'a decorative template page — its frames are not the standard grid; place this one by hand' }
        : { kind: 'free', page: pageNo, maxChars: MAX_HOLDER_LINE };
      continue;
    }
    // Taken: by a photo NOT being captioned now, or by one placed earlier in this pass.
    const taken = (e: PageEl) => claimed.has(e.id) || (Boolean(e.captionFor) && e.captionFor !== id && !ids.has(String(e.captionFor)));
    let hand = false, small = false;
    let pick: CaptionTarget | null = null;
    for (const { e } of holderCands(pg, id, taken)) {
      if (handWords(e) && !opts.replaceHandText) { if (!pageBoxRole(e)) hand = true; continue; }
      const cap = holderCapacity(styledHolder(e, opts.fontFamily), langs);
      if (!cap) { small = true; continue; }
      pick = { kind: 'holder', page: pageNo, elementId: e.id, role: String(e.holder), mode: cap.mode, maxChars: cap.maxChars };
      claimed.add(e.id);
      break;
    }
    if (pick) { out[id] = pick; continue; }
    // (A holder of this photo a person wrote in: the photo has its words — nothing more is added.)
    if (opts.themedFree !== false && !hand) {
      const look = freeLook(pages, at.pi, { fontFamily: opts.fontFamily });
      const safe = pageSafeBox(pageNo, themedBook);
      // This tool's free captions of the photos captioned now are replaced: they do not block.
      const els = [...(pg.elements ?? []).filter((e) => !(isCaption(e) && ids.has(e.captionFor))), ...(planned.get(at.pi) ?? [])];
      const page = { ...pg, elements: els };
      const cap = freeCaptionCapacity(page, id, langs, { safe, fontSize: freeSize, fontFamily: look.fontFamily, align: look.align });
      if (cap) {
        // Reserve the spot the words will take (two rows a language: the most they may need).
        const fontMm = freeSize / 2;
        const plan = findFreeSpot(page, id, () => freeHeightMm(FREE_MAX_ROWS * Math.max(1, langs.length), fontMm), { safe, align: look.align })
          ?? findFreeSpot(page, id, () => freeHeightMm(Math.max(1, langs.length), fontMm), { safe, align: look.align });
        if (plan) planned.set(at.pi, [...(planned.get(at.pi) ?? []), { id: `plan-${id}`, type: 'text', ...plan.box, text: 'x' }]);
        out[id] = { kind: 'free', page: pageNo, maxChars: cap.maxChars, where: 'paper', spot: cap.spot };
        continue;
      }
      const slot = slotBoxes(pg, 0)[at.si];
      const onDecor = slot && usableIn(slot, safe) ? null
        : freeCaptionCapacity(page, id, langs, { safe, fontSize: freeSize, fontFamily: look.fontFamily, align: look.align, overDecor: true });
      out[id] = slot && usableIn(slot, safe)
        ? { kind: 'free', page: pageNo, maxChars: MAX_HOLDER_LINE, where: 'photo' }
        : onDecor
          ? { kind: 'free', page: pageNo, maxChars: onDecor.maxChars, where: 'paper', spot: onDecor.spot }
          : { kind: 'none', page: pageNo, reason: 'no_room', why: 'no free place next to this photo inside the print safe zone (12 mm from the edges, 15 mm from the spine)' };
      continue;
    }
    out[id] = hand
      ? { kind: 'none', page: pageNo, reason: 'text', why: 'its caption box already holds words a person wrote — « Remplacer les textes » is off, so they are kept' }
      : small
        ? { kind: 'none', page: pageNo, reason: 'too_long', why: `its text box is too small for ${langs.length} languages — choose fewer, or write it by hand` }
        : { kind: 'none', page: pageNo, reason: 'no_holder', why: 'this model has no text box for this photo' };
  }
  return out;
}

/**
 * The words this tool wrote for each photo (its holder's or its free
 * caption's), one line per language — what the dialog shows before anything
 * is written again.
 */
export function existingCaptions(pages: CaptionPage[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of pages) {
    for (const e of p.elements ?? []) {
      if (!e || e.type !== 'text' || typeof e.captionFor !== 'string' || !e.captionFor) continue;
      const t = String(e.text ?? '');
      out[e.captionFor] = e.holder && !t.includes('\n') && t.includes(INLINE_SEP) ? t.split(INLINE_SEP).join('\n') : t;
    }
  }
  return out;
}

/**
 * The album's « Lieu · date » words: the line this tool wrote (`own`), else the
 * one its model holders print most (the theme wrote it from the order) — the
 * dialog's starting value. '' when none.
 */
export function placeLineOf(pages: CaptionPage[], own = false): string {
  const count = new Map<string, number>();
  for (const pass of own ? [true] : [true, false]) {
    for (const p of pages) {
      for (const e of p.elements ?? []) {
        if (!e || e.type !== 'text' || e.holder !== 'lieu_date' || (pass && !e.captionPlace)) continue;
        const t = String(e.text ?? '').trim();
        if (t) count.set(t, (count.get(t) ?? 0) + 1);
      }
    }
    if (count.size) break;
  }
  return [...count.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
}

/** Does the album have empty « Lieu · date » holders on these pages (or ones this tool filled)? */
export function placeHolderCount(pages: CaptionPage[], pageNumbers?: Iterable<number> | null): number {
  const want = pageNumbers ? new Set(pageNumbers) : null;
  let n = 0;
  for (const p of pages) {
    if (!p.theme || (want && !want.has(Number(p.page)))) continue;
    for (const e of p.elements ?? []) if (e && e.type === 'text' && e.holder === 'lieu_date' && (!String(e.text ?? '').trim() || e.captionPlace)) n += 1;
  }
  return n;
}

const foldWords = (s: string) => String(s ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * « Retirer » — what this tool wrote, taken off: the free captions of these
 * photos deleted, their holders back to empty with the model's own font,
 * colour and size, and the « Lieu · date » it filled on these pages emptied.
 * A person's own words are never touched. Pure; the SAME array when nothing changed.
 */
export function uncaption(pages: CaptionPage[], photoIds: Iterable<string>, pageNumbers: Iterable<number> = []): CaptionPage[] {
  const ids = new Set(photoIds);
  const pnos = new Set(pageNumbers);
  if (!ids.size && !pnos.size) return pages;
  let changed = false;
  const out = pages.map((p) => {
    const els = p.elements ?? [];
    let touched = false;
    const next: PageEl[] = [];
    for (const e of els) {
      if (isCaption(e) && ids.has(e.captionFor)) { touched = true; continue; }
      if (e && e.type === 'text' && e.holder && ((typeof e.captionFor === 'string' && ids.has(e.captionFor)) || (e.captionPlace && pnos.has(Number(p.page))))) {
        touched = true; next.push(emptied(e)); continue;
      }
      next.push(e);
    }
    if (!touched) return p;
    changed = true;
    return { ...p, elements: next };
  });
  return changed ? out : pages;
}

// ─── ONE CAPTION PER PAGE (owner, 2026-10-15) ───────────────────────────────
//
// « If there are several photos on one page, take the context of all of them
// and write one caption — and some captions must replace the sous-titre or the
// section text. » A page caption goes into one of the model's PAGE text boxes
// (Sous-titre, Section, or the page's Légende that belongs to no photo),
// REPLACING what the theme wrote there; the theme's words and look are kept
// on the box (`captionOrigText`, `captionOrig`) and come back with « Retirer ».

/** The model boxes a page caption may take, best first. */
export const PAGE_CAPTION_ROLES = ['sous_titre', 'section', 'legende'] as const;
export const PAGE_ROLE_FR: Record<string, string> = { sous_titre: 'Sous-titre', section: 'Section', legende: 'Légende de la page' };

export interface PageCaptionBox {
  elementId: string; role: string; label: string;
  /** What the box says now (the page caption when this tool wrote one). */
  text: string;
  /** The words the theme (or a person) had there before this tool. */
  origText: string;
}

/** The page's own text boxes a page caption may go in (a model page only). */
export function pageCaptionBoxes(page: CaptionPage): PageCaptionBox[] {
  if (!page.theme) return [];
  const out: PageCaptionBox[] = [];
  for (const role of PAGE_CAPTION_ROLES) {
    for (const e of page.elements ?? []) {
      if (!e || e.type !== 'text' || e.holder !== role) continue;
      if (role === 'legende' && (e.holderFor || e.captionFor)) continue;
      out.push({
        elementId: e.id, role, label: PAGE_ROLE_FR[role] ?? role,
        text: String(e.text ?? ''),
        origText: e.captionPageOf != null || e.captionFor ? String(e.captionOrigText ?? '') : String(e.text ?? ''),
      });
    }
  }
  return out;
}

/** How long each language line may be in this box (the person's font, when chosen). */
export function pageCaptionCapacity(page: CaptionPage, elementId: string, langs: ReadonlyArray<string>, fontFamily?: string | null): { mode: 'stack' | 'inline'; maxChars: number } | null {
  const e = (page.elements ?? []).find((x) => x && x.id === elementId);
  return e ? holderCapacity(styledHolder(e, fontFamily), langs) : null;
}

/** The page captions this tool wrote: page number → its box and words (one line per language). */
export function existingPageCaptions(pages: CaptionPage[]): Record<number, { elementId: string; text: string }> {
  const out: Record<number, { elementId: string; text: string }> = {};
  for (const p of pages) {
    for (const e of p.elements ?? []) {
      if (!e || e.type !== 'text' || e.captionPageOf == null) continue;
      const t = String(e.text ?? '');
      out[Number(p.page)] = { elementId: e.id, text: !t.includes('\n') && t.includes(INLINE_SEP) ? t.split(INLINE_SEP).join('\n') : t };
    }
  }
  return out;
}

/** A box this tool wrote a page caption in, back to the theme's words and look. */
function restoredPageBox(e: PageEl): PageEl {
  const o = (e.captionOrig ?? null) as HolderOrig | null;
  const { captionPageOf: _p, captionOrig: _o, captionOrigText: t, ...rest } = e;
  void _p; void _o;
  const back: Record<string, unknown> = { ...rest, text: String(t ?? '') };
  if (o) {
    for (const k of ORIG_KEYS) {
      if (!(k in o)) continue;
      if (o[k] === null || o[k] === undefined) delete back[k];
      else back[k] = o[k];
    }
  }
  return back as PageEl;
}

/** « Retirer » for page captions: the boxes of these pages get the theme's words back. The SAME array when nothing changed. */
export function unpageCaption(pages: CaptionPage[], pageNumbers: Iterable<number>): CaptionPage[] {
  const want = new Set([...pageNumbers].map(Number));
  if (!want.size) return pages;
  let changed = false;
  const out = pages.map((p) => {
    if (!want.has(Number(p.page)) || !(p.elements ?? []).some((e) => e && e.captionPageOf != null)) return p;
    changed = true;
    return { ...p, elements: (p.elements ?? []).map((e) => (e && e.captionPageOf != null ? restoredPageBox(e) : e)) };
  });
  return changed ? out : pages;
}

/**
 * Write each page's caption into its chosen box: only the words, the size
 * (between the role's sizes) and — when the person chose them — the font and
 * ink change. A page's previous page caption is replaced, never doubled. Pure.
 */
export function writePageCaptions(
  pages: CaptionPage[],
  requests: Array<{ page: number; elementId: string; text: string }>,
  opts: { fontFamily?: string | null; fontColor?: string | null } = {},
): { pages: CaptionPage[]; placed: Array<{ page: number; elementId: string; role: string }>; skipped: Array<{ page: number; why: string }> } {
  const wanted = requests.filter((r) => r && lineList(r.text).length);
  let out = unpageCaption(pages, wanted.map((r) => r.page));
  const placed: Array<{ page: number; elementId: string; role: string }> = [];
  const skipped: Array<{ page: number; why: string }> = [];
  for (const r of wanted) {
    const pi = out.findIndex((p) => Number(p.page) === Number(r.page));
    const els = pi >= 0 ? (out[pi].elements ?? []) : [];
    const k = els.findIndex((e) => e && e.id === r.elementId);
    if (k < 0) { skipped.push({ page: r.page, why: 'the model text box is no longer on this page' }); continue; }
    // A photo's caption in this box (one caption per photo) gives way: the box's own words first.
    const e = isToolHolder(els[k]) ? emptied(els[k]) : els[k];
    const styled = styledHolder(e, opts.fontFamily, opts.fontColor);
    const fit = fitHolder(lineList(r.text).join('\n'), styled);
    if (!fit) {
      const cap = holderCapacity(styled, scriptsOf(lineList(r.text)));
      skipped.push({ page: r.page, why: `the words do not fit the « ${PAGE_ROLE_FR[String(e.holder)] ?? 'text'} » box of this model${cap ? ` (≈ ${cap.maxChars} characters a line)` : ''} — shorten them` });
      continue;
    }
    const filled: PageEl = { ...styled, text: fit.text, fontSize: fit.fontSize, captionPageOf: Number(r.page), captionOrig: origOf(e), captionOrigText: String(e.text ?? '') };
    out = out.map((p, i) => (i === pi ? { ...p, elements: els.map((x, j) => (j === k ? filled : x)) } : p));
    placed.push({ page: Number(r.page), elementId: e.id, role: String(e.holder) });
  }
  return { pages: out, placed, skipped };
}

export interface CaptionAlbumResult {
  pages: CaptionPage[];
  /** `spot` (model pages): where a caption with no holder went — next to the photo, or on it. */
  placed: Array<{ photoId: string; page: number; element: CaptionElement; band: 'top' | 'bottom'; holder?: string; spot?: FreeSpot | 'photo' }>;
  skipped: Array<{ photoId: string; page: number | null; reason: SkipReason; why: string }>;
  fontSize: number;
  /** « Lieu · date » holders filled with the place line. */
  places: number;
}

export interface CaptionAlbumOpts {
  gapMm?: GapSpec; luma?: LumaSampler; replaceHandText?: boolean; idFor?: (photoId: string) => string;
  style?: CaptionStyle | null; fontSize?: number;
  /** A model page's photo with no holder of its own gets a free caption on the paper next to it (on it when no room). Default true; « Ne pas légender les photos sans cadre » turns it off. */
  themedFree?: boolean;
  /** The person's font and ink for the words written now (absent = the model's own on a holder, the album's on a photo). */
  fontFamily?: string | null;
  fontColor?: string | null;
  /** The album's « Lieu · date » line, written in the empty Lieu · date holders of `placePages` (all pages when absent). */
  placeLine?: string | null;
  placePages?: number[] | null;
}

/**
 * Caption these photos. Each photo's previous caption (wherever it is) is
 * REPLACED, never doubled. `replaceHandText` also removes the hand-written
 * texts on the pages being captioned — the editor offers it, the agent never
 * does. Pure: returns a new album, the input is not touched.
 */
export function captionAlbum(
  pages: CaptionPage[],
  requests: Array<{ photoId: string; text: string }>,
  photos: Record<string, CaptionPhoto | undefined>,
  opts: CaptionAlbumOpts = {},
): CaptionAlbumResult {
  opts = { ...opts, gapMm: gapForBook(opts.gapMm, pages) };
  const themedFree = opts.themedFree !== false;
  const themedBook = isThemedBook(pages as ReadonlyArray<{ theme?: unknown; model?: unknown }>);
  const freeSize = freeCaptionSize(pages);
  const wanted = requests.filter((r) => r && r.photoId && String(r.text ?? '').trim());
  const ids = new Set(wanted.map((r) => r.photoId));
  const fontSize = opts.fontSize && opts.fontSize > 0 ? opts.fontSize : captionFontSize(pages, [...ids], opts.gapMm ?? 0);
  const pagesOf = new Set(wanted.map((r) => locate(pages, r.photoId)?.pi).filter((n): n is number => n !== undefined));
  const placeLine = String(opts.placeLine ?? '').replace(/\s+/g, ' ').trim();
  const placePages = placeLine ? new Set((opts.placePages ?? pages.map((p) => Number(p.page))).map(Number)) : new Set<number>();
  // The words written before for these photos (and the place line about to be written again) come off first.
  let out: CaptionPage[] = uncaption(pages, ids, placePages).map((p, pi) => {
    const els = p.elements ?? [];
    const keep = els.filter((e) => !(opts.replaceHandText && pagesOf.has(pi) && e.type === 'text' && !isCaption(e) && !e.holder));
    return keep.length === els.length ? p : { ...p, elements: keep };
  });
  const result: CaptionAlbumResult = { pages: out, placed: [], skipped: [], fontSize, places: 0 };
  // In album order, so a page's first caption is placed before its second.
  const ordered = [...wanted].sort((a, b) => {
    const la = locate(out, a.photoId), lb = locate(out, b.photoId);
    return (la ? la.pi * 10 + la.si : 1e9) - (lb ? lb.pi * 10 + lb.si : 1e9);
  });
  for (const r of ordered) {
    const at = locate(out, r.photoId);
    if (!at) { result.skipped.push({ photoId: r.photoId, page: null, reason: 'not_in_album', why: 'this photo is not in the album' }); continue; }
    const pg = out[at.pi];
    // ─── A THEMED PAGE WRITES INTO ITS CAPTION HOLDER (design system v2 §4) ──
    // « Légendes IA writes into a caption holder when the page has one
    // (position, font, colour stay). » The holder of THIS photo first, then the
    // page's own Légende. Only the words and the size (between the role's
    // largest and smallest) change — and the font / ink when the person chose
    // their own; the model's are kept on the holder (`captionOrig`) for « Retirer ».
    if (pg.theme) {
      const pick = captionHolderFor(pg, r.photoId, r.text, opts.replaceHandText === true, opts.fontFamily);
      if (pick.kind === 'holder') {
        const els = pg.elements ?? [];
        const e = els[pick.index];
        const was = String(e.text ?? '');
        const filled = {
          ...styledHolder(e, opts.fontFamily, opts.fontColor), text: pick.text, captionFor: r.photoId, fontSize: pick.fontSize, captionOrig: origOf(e),
          ...(was.trim() ? { captionOrigText: was } : {}),
        };
        out = out.map((p, pi) => (pi === at.pi ? { ...p, elements: els.map((x, k) => (k === pick.index ? filled : x)) } : p));
        result.placed.push({ photoId: r.photoId, page: Number(pg.page), element: filled as unknown as CaptionElement, band: 'bottom', holder: String(e.holder) });
        continue;
      }
      if (pick.kind === 'skip' && !(themedFree && pick.reason === 'too_long')) {
        result.skipped.push({ photoId: r.photoId, page: Number(pg.page), reason: pick.reason, why: pick.why });
        continue;
      }
      if (!themedFree) {
        result.skipped.push({ photoId: r.photoId, page: Number(pg.page), reason: 'no_holder', why: 'this model has no text box for this photo' });
        continue;
      }
      // ─── NO HOLDER: A FREE CAPTION ON THE PAPER (owner, 2026-10-14) ────────
      const freeOpts = {
        fontSize: freeSize, look: freeLook(out, at.pi, { fontFamily: opts.fontFamily, fontColor: opts.fontColor }),
        themedBook, id: opts.idFor?.(r.photoId),
      };
      const putFree = (free: FreePlaceResult & { ok: true }) => {
        out = out.map((p, pi) => (pi === at.pi ? { ...p, elements: [...(p.elements ?? []), free.element] } : p));
        result.placed.push({ photoId: r.photoId, page: Number(pg.page), element: free.element, band: free.spot === 'above' ? 'top' : 'bottom', spot: free.spot });
      };
      const free = placeFreeCaption(out, at.pi, r.photoId, r.text, freeOpts);
      if (free.ok) { putFree(free); continue; }
      if (free.reason !== 'no_room') {
        result.skipped.push({ photoId: r.photoId, page: Number(pg.page), reason: free.reason, why: free.why });
        continue;
      }
      // No clean spot on the paper: the old caption ON the photo, inside the page's safe zone, off faces —
      // in the MODEL's caption font (freeLook), never the grid's Londrina when the model has its own (#1248).
      const modelFont = freeOpts.look.fontFamily !== CAPTION_FONT_FAMILY ? freeOpts.look.fontFamily : null;
      const fb = onPhotoFallback(pg, r.photoId, r.text, photos[r.photoId], {
        fontSize, gapMm: opts.gapMm, luma: opts.luma, id: opts.idFor?.(r.photoId), style: opts.style,
        fontFamily: opts.fontFamily || modelFont, fontColor: opts.fontColor, themedBook,
      });
      if (fb.ok) {
        out = out.map((p, pi) => (pi === at.pi ? { ...p, elements: [...(p.elements ?? []), fb.element] } : p));
        result.placed.push({ photoId: r.photoId, page: Number(pg.page), element: fb.element, band: fb.band, spot: 'photo' });
        continue;
      }
      // Last: the paper, over a decoration's main body (never over a window, a face or a text).
      const overDecor = placeFreeCaption(out, at.pi, r.photoId, r.text, { ...freeOpts, overDecor: true });
      if (overDecor.ok) { putFree(overDecor); continue; }
      result.skipped.push({ photoId: r.photoId, page: Number(pg.page), reason: fb.reason, why: fb.why });
      continue;
    }
    const photo = photos[r.photoId];
    if (!photo || !(photo.width > 0) || !(photo.height > 0)) {
      result.skipped.push({ photoId: r.photoId, page: Number(pg.page), reason: 'no_photo_size', why: 'the size of this photo is unknown, so its faces cannot be avoided' });
      continue;
    }
    const placed = placeCaption(pg, r.photoId, r.text, photo, {
      fontSize, gapMm: opts.gapMm, luma: opts.luma, id: opts.idFor?.(r.photoId), style: opts.style,
      fontFamily: opts.fontFamily, fontColor: opts.fontColor,
    });
    if (!placed.ok) { result.skipped.push({ photoId: r.photoId, page: Number(pg.page), reason: placed.reason, why: placed.why }); continue; }
    out = out.map((p, pi) => (pi === at.pi ? { ...p, elements: [...(p.elements ?? []), placed.element] } : p));
    result.placed.push({ photoId: r.photoId, page: Number(pg.page), element: placed.element, band: placed.band });
  }
  // ─── « LIEU · DATE » (page holders that belong to no photo) ─────────────────
  // The place and date the client gave, in the model's empty Lieu · date
  // holders — never the same words twice on one page, never a person's words
  // (or the theme's own) replaced.
  if (placeLine) {
    out = out.map((p) => {
      if (!p.theme || !placePages.has(Number(p.page))) return p;
      const next: PageEl[] = [...(p.elements ?? [])];
      let changed = false;
      for (let k = 0; k < next.length; k += 1) {
        const e = next[k];
        if (!(e && e.type === 'text' && e.holder === 'lieu_date') || String(e.text ?? '').trim()) continue;
        const said = next.some((x, j) => j !== k && x && x.type === 'text' && String(x.text ?? '').trim() && foldWords(String(x.text)) === foldWords(placeLine));
        if (said) continue;
        const styled = styledHolder(e, opts.fontFamily, opts.fontColor);
        const size = holderFontSize(placeLine, styled, 2);
        if (size == null) continue;
        next[k] = { ...styled, text: placeLine, fontSize: size, captionPlace: true, captionOrig: origOf(e) };
        changed = true;
        result.places += 1;
      }
      return changed ? { ...p, elements: next } : p;
    });
  }
  result.pages = out;
  return result;
}

/**
 * Captions follow their PHOTOS, and never fight a PERSON. A caption whose photo
 * left its page (a swap, a drag, a re-layout) or changed slot on it is placed
 * again on the photo's page — same words, same style — or removed when the
 * photo left the album. A caption whose photo has NOT moved stays exactly where
 * it is, wherever a person dragged it. (A caption from before `captionSlot`
 * is home while its centre is inside its photo's slot.)
 *
 * Returns the SAME array when nothing had to move, so a caller may run it on
 * every change without a loop.
 */
export function followCaptions(
  pages: CaptionPage[],
  photos: Record<string, CaptionPhoto | undefined>,
  opts: { gapMm?: GapSpec } = {},
): CaptionPage[] {
  opts = { ...opts, gapMm: gapForBook(opts.gapMm, pages) };
  const themedBook = isThemedBook(pages as ReadonlyArray<{ theme?: unknown; model?: unknown }>);
  const strays: Array<{ from: number; el: CaptionElement }> = [];
  pages.forEach((p, pi) => {
    for (const e of p.elements ?? []) {
      if (!isCaption(e)) continue;
      const si = (p.photos ?? []).indexOf(e.captionFor);
      let home: boolean;
      if (si < 0) home = false;                                  // its photo is on another page now
      else if (typeof e.captionSlot === 'number') home = si === e.captionSlot && (!e.captionLayout || e.captionLayout === p.layout);
      else {
        const slot = slotBoxes(p, opts.gapMm ?? 0)[si];
        const centreX = Number(e.x) + Number(e.width) / 2, centreY = Number(e.y) + Number(e.height) / 2;
        home = Boolean(slot) && centreX >= slot.x && centreX <= slot.x + slot.width && centreY >= slot.y && centreY <= slot.y + slot.height;
      }
      if (!home) strays.push({ from: pi, el: e });
    }
  });
  if (!strays.length) return pages;
  const strayIds = new Set(strays.map((s) => s.el.id));
  let out: CaptionPage[] = pages.map((p) => {
    const els = p.elements ?? [];
    return els.some((e) => strayIds.has(e.id)) ? { ...p, elements: els.filter((e) => !strayIds.has(e.id)) } : p;
  });
  for (const { el } of strays) {
    const at = locate(out, el.captionFor);
    if (!at) continue;                                   // the photo left the album: so do its words
    const photo = photos[el.captionFor] ?? { id: el.captionFor, width: 0, height: 0, faces: [] };
    // To a model page: on the paper next to the photo, in the caption's own look.
    if (out[at.pi].theme && out[at.pi].blank) {
      const look = el.captionFree
        ? { fontFamily: el.fontFamily, fontColor: el.fontColor, fontWeight: el.fontWeight, fontStyle: String(el.fontStyle ?? 'normal'), align: freeLook(out, at.pi).align }
        : freeLook(out, at.pi);
      const fo = { fontSize: el.captionFree ? el.fontSize : freeCaptionSize(out), look, themedBook, id: el.id };
      let free = placeFreeCaption(out, at.pi, el.captionFor, el.text, fo);
      if (!free.ok && el.captionFree) free = placeFreeCaption(out, at.pi, el.captionFor, el.text, { ...fo, overDecor: true });
      if (free.ok) {
        out = out.map((p, pi) => (pi === at.pi ? { ...p, elements: [...(p.elements ?? []), free.element] } : p));
        continue;
      }
    }
    const style: CaptionStyle | null = el.bandFill ? { band: el.bandFill, ink: el.fontColor, opacity: el.bandOpacity } : null;
    // The person's own font (« Police » of the dialog) is measured as theirs and kept.
    const ownFont = el.fontFamily && el.fontFamily !== CAPTION_FONT_FAMILY && el.fontFamily !== BAND_FONT_FAMILY ? el.fontFamily : null;
    const modelSafe = out[at.pi].theme ? pageSafeBox(Number(out[at.pi].page), themedBook) : undefined;
    const placed = placeCaption(out[at.pi], el.captionFor, el.text, photo, { fontSize: el.fontSize, gapMm: opts.gapMm, id: el.id, style, fontFamily: ownFont, safe: modelSafe });
    // The words are never lost: when no band is free, the caption keeps its
    // style at the bottom of the photo's slot for a person to move.
    const next: CaptionElement = placed.ok
      ? { ...el, ...placed.element, fontColor: el.fontColor, outlineColor: el.outlineColor, fontFamily: el.fontFamily, fontWeight: el.fontWeight }
      : { ...el, ...bottomOf(out[at.pi], at.si, el, opts.gapMm ?? 0, modelSafe), captionSlot: at.si, captionLayout: out[at.pi].layout };
    out = out.map((p, pi) => (pi === at.pi ? { ...p, elements: [...(p.elements ?? []), next] } : p));
  }
  return out;
}

function bottomOf(page: CaptionPage, si: number, el: CaptionElement, gapMm: GapSpec, safe?: Box): Pick<Box, 'x' | 'y'> {
  const slot = slotBoxes(page, gapMm)[si];
  const usable = slot ? usableIn(slot, safe) : null;
  if (!usable) return { x: el.x, y: el.y };
  return { x: r2(usable.x + Math.max(0, (usable.width - el.width) / 2)), y: r2(Math.max(usable.y, usable.y + usable.height - el.height)) };
}

// ─── FREE CAPTIONS ON MODEL PAGES (owner, 2026-10-14) ───────────────────────
//
// « If there is no caption or text on a model, Légendes IA can still add a
// text and place it in a good position that matches the whole album and page —
// just not near the edges, we might cut it in production. »
//
//   · WHERE: on the page's paper next to its photo — under it, else above it,
//     else beside it — a few mm from the photo's window (its frame included),
//     aligned like the model's own text boxes on that page (left edge, centre,
//     right edge of the photo). Never over a photo window (so never over a
//     face), never over any text or text box, and over a decoration's main
//     body only when nothing else is free.
//   · SAFE ZONE: the whole box at least BASE_MARGIN_MM (12) from every trim
//     edge and BASE_SPINE_MM (15) from the spine side (bookStructure: album
//     page 1 is a LEFT page — its spine is on its right).
//   · LOOK: the model's caption font and ink (the page's own holders, else the
//     same theme's elsewhere in the album), ONE size for the album, at most
//     FREE_MAX_ROWS rows per language. The length the vision model is asked to
//     keep each line to comes from the box chosen (freeCaptionCapacity).
//   · No clean spot: the old caption ON the photo, still inside the safe zone
//     and off faces; no room even there: the photo is reported (« no_room »).

/** Air between the photo's window and its words. */
export const FREE_GAP_MM = 3;
/** Air kept from any other thing on the page. */
export const FREE_PAD_MM = 2;
/** A caption box under / above its photo: this wide at most, and at least. */
export const FREE_MAX_W_MM = 110;
export const FREE_MIN_W_MM = 40;
/** A caption box beside its photo. */
export const FREE_SIDE_MAX_W_MM = 70;
export const FREE_SIDE_MIN_W_MM = 32;
/** Rows a language line may wrap to. */
export const FREE_MAX_ROWS = 2;
/** The album's free-caption size (element units, 2 = 1 mm) when its models say nothing, and its bounds. */
export const FREE_SIZE_DEFAULT = 7.5;
export const FREE_SIZE_MIN = 6.5;
export const FREE_SIZE_MAX = 10;

export type FreeSpot = 'below' | 'above' | 'left' | 'right';
type Align = 'left' | 'center' | 'right';

/**
 * Where a free caption may be on album page `pageNo` (page %): the trim less
 * the base rules' margins — 12 mm, and 15 mm on the spine side.
 */
export function pageSafeBox(pageNo: number, themedBook = false): Box {
  const f = baseFrameMm(spineIndex((Math.round(Number(pageNo)) || 1) - 1, themedBook));
  return { x: mmX(f.left), y: mmY(f.top), width: 100 - mmX(f.left) - mmX(f.right), height: 100 - mmY(f.top) - mmY(f.bottom) };
}

const within = (b: Box, s: Box): boolean => b.x >= s.x - 1e-6 && b.y >= s.y - 1e-6
  && b.x + b.width <= s.x + s.width + 1e-6 && b.y + b.height <= s.y + s.height + 1e-6;

/** An element's box on the page, turned elements by the box around them (page %). */
export function boundsOf(e: Pick<PageEl, 'x' | 'y' | 'width' | 'height'> & { rotation?: unknown }): Box {
  const b = { x: Number(e.x), y: Number(e.y), width: Number(e.width), height: Number(e.height) };
  const rot = ((Number(e.rotation) || 0) * Math.PI) / 180;
  if (!rot) return b;
  const w = (b.width / 100) * PAGE_W_MM, h = (b.height / 100) * PAGE_H_MM;
  const W = Math.abs(w * Math.cos(rot)) + Math.abs(h * Math.sin(rot)), H = Math.abs(w * Math.sin(rot)) + Math.abs(h * Math.cos(rot));
  const cx = b.x + b.width / 2, cy = b.y + b.height / 2;
  return { x: cx - mmX(W) / 2, y: cy - mmY(H) / 2, width: mmX(W), height: mmY(H) };
}
const union = (bs: Box[]): Box => {
  const x0 = Math.min(...bs.map((b) => b.x)), y0 = Math.min(...bs.map((b) => b.y));
  const x1 = Math.max(...bs.map((b) => b.x + b.width)), y1 = Math.max(...bs.map((b) => b.y + b.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
};
/** A decoration's main body: the middle of its box (a piece's picture rarely fills it to the edge). */
const core = (b: Box): Box => ({ x: b.x + b.width * 0.15, y: b.y + b.height * 0.15, width: b.width * 0.7, height: b.height * 0.7 });

/** Decorations (pieces, stickers): avoided when possible. Paper layers are paper. Everything else is never covered. */
const DECOR_LAYERS = new Set(['object', 'tape', 'clip', 'label', 'mark']);
const isDecor = (e: PageEl) => e.type === 'icon' && (!e.layer || DECOR_LAYERS.has(String(e.layer)));
const isPaper = (e: PageEl) => e.layer === 'paper';

/**
 * The elements that make photo `photoId`'s window: the photo and the frame
 * pieces of its group drawn around IT (a frame whose box holds the photo's
 * centre — a polaroid, a mount). The group's other photos are not part of it.
 */
function windowParts(page: Pick<CaptionPage, 'elements'>, photoId: string): PageEl[] {
  const els = page.elements ?? [];
  const own = els.find((e) => e && e.type === 'photo' && e.photoId === photoId);
  if (!own) return [];
  const grp = own.group;
  const ob = boundsOf(own);
  const cx = ob.x + ob.width / 2, cy = ob.y + ob.height / 2;
  return els.filter((e) => e && (e === own || (grp != null && e.group === grp && e.type !== 'photo' && !isPaper(e)
    && (() => { const b = boundsOf(e); return cx >= b.x && cx <= b.x + b.width && cy >= b.y && cy <= b.y + b.height; })())));
}

/** The page's photo window for `photoId` (page %): the photo and its frame. */
export function photoWindow(page: Pick<CaptionPage, 'elements'>, photoId: string): Box | null {
  const parts = windowParts(page, photoId);
  return parts.length ? union(parts.map(boundsOf)) : null;
}

const CAPTION_ROLE_ORDER = ['legende', 'mot_manuscrit', 'mot_photo'];
const holdersIn = (p: CaptionPage | undefined) => (p?.elements ?? []).filter((e) => e && e.type === 'text' && e.holder);
/** Is a holder written on the page's paper (not on a label, a polaroid chin, a paper layer)? */
function onPaper(page: CaptionPage, h: PageEl): boolean {
  const cx = Number(h.x) + Number(h.width) / 2, cy = Number(h.y) + Number(h.height) / 2;
  return !(page.elements ?? []).some((e) => e && e !== h && (e.type === 'photo' || e.type === 'rect' || (e.type === 'icon' && (e.layer === 'label' || e.layer === 'paper' || e.layer === 'frame')))
    && (() => { const b = boundsOf(e); return cx >= b.x && cx <= b.x + b.width && cy >= b.y && cy <= b.y + b.height; })());
}

/** The holder whose face the album's holders use most (ties: the first) — one face for every page. */
function commonest(hs: PageEl[]): PageEl | undefined {
  const n = new Map<string, number>();
  for (const h of hs) n.set(String(h.fontFamily ?? ''), (n.get(String(h.fontFamily ?? '')) ?? 0) + 1);
  let best: PageEl | undefined;
  for (const h of hs) if (h.fontFamily && (!best || (n.get(String(h.fontFamily)) ?? 0) > (n.get(String(best.fontFamily)) ?? 0))) best = h;
  return best;
}

export interface FreeLook { fontFamily: string; fontColor: string; fontWeight: string; fontStyle: string; align: Align }

/**
 * The model's caption look for page `pi`: the font of the theme's Légende
 * (this page's, else elsewhere in the album — one face for the album), else
 * of its other caption holders, else the face the album's holders use most; the ink of a holder written on the paper; the alignment
 * most of its holders have. The person's own font / ink win.
 */
export function freeLook(pages: CaptionPage[], pi: number, own: { fontFamily?: string | null; fontColor?: string | null } = {}): FreeLook {
  const page = pages[pi];
  const sameTheme = pages.filter((p, i) => i !== pi && p.theme && p.theme === page?.theme);
  const byRole = (hs: PageEl[]) => [...hs].filter((h) => CAPTION_ROLE_ORDER.includes(String(h.holder)))
    .sort((a, b) => CAPTION_ROLE_ORDER.indexOf(String(a.holder)) - CAPTION_ROLE_ORDER.indexOf(String(b.holder)));
  const mine = holdersIn(page);
  // The Légende's face first — this page's, else the theme's anywhere in the album — so the album's
  // free captions share ONE face; then the page's other caption holders, the theme's, any holder of the page.
  const legende = (hs: PageEl[]) => hs.find((h) => h.holder === 'legende');
  const fontFrom = legende(mine) ?? sameTheme.map((p) => legende(holdersIn(p))).find(Boolean)
    ?? byRole(mine)[0] ?? sameTheme.map((p) => byRole(holdersIn(p))[0]).find(Boolean) ?? commonest([...mine, ...sameTheme.flatMap(holdersIn)]);
  const paperHolders = page ? mine.filter((h) => onPaper(page, h)) : [];
  const inkFrom = byRole(paperHolders)[0] ?? paperHolders[0]
    ?? sameTheme.map((p) => { const hs = holdersIn(p).filter((h) => onPaper(p, h)); return byRole(hs)[0] ?? hs[0]; }).find(Boolean)
    ?? fontFrom;
  const aligns = mine.map((h) => String(h.textAlign ?? 'center'));
  const count = (a: string) => aligns.filter((x) => x === a).length;
  const align: Align = count('left') > count('center') && count('left') >= count('right') ? 'left'
    : count('right') > count('center') && count('right') > count('left') ? 'right' : 'center';
  const ownFont = String(own.fontFamily ?? '').trim(), ownInk = String(own.fontColor ?? '').trim();
  return {
    fontFamily: ownFont || String(fontFrom?.fontFamily ?? '') || CAPTION_FONT_FAMILY,
    fontColor: ownInk || String(inkFrom?.fontColor ?? '') || INK_DARK,
    fontWeight: ownFont ? '400' : String(fontFrom?.fontWeight ?? (fontFrom ? '400' : CAPTION_FONT_WEIGHT)),
    fontStyle: ownFont ? 'normal' : String(fontFrom?.fontStyle ?? 'normal'),
    align,
  };
}

/**
 * ONE size for every free caption of the album: the one they already have,
 * else the model's own caption size — halfway between its Légende's largest
 * and smallest (the size its holders print most), within FREE_SIZE_MIN..MAX.
 */
export function freeCaptionSize(pages: CaptionPage[]): number {
  for (const p of pages) for (const e of p.elements ?? []) if (isCaption(e) && e.captionFree && Number(e.fontSize) > 0) return Number(e.fontSize);
  for (const role of CAPTION_ROLE_ORDER) {
    const count = new Map<number, number>();
    for (const p of pages) for (const h of holdersIn(p)) {
      if (h.holder !== role) continue;
      const max = Number(h.fontSizeMax ?? h.fontSize), min = Number(h.fontSizeMin ?? max);
      const s = Math.round(((max + Math.min(max, min > 0 ? min : max)) / 2) * 10) / 10;
      if (s > 0) count.set(s, (count.get(s) ?? 0) + 1);
    }
    const top = [...count.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0];
    if (top) return Math.min(FREE_SIZE_MAX, Math.max(FREE_SIZE_MIN, Math.round(top[0] * 10) / 10));
  }
  return FREE_SIZE_DEFAULT;
}

/** Rows one written line takes in a box `wMm` wide (errs wide: the words are never cut). */
function freeRows(line: string, family: string, fontMm: number, wMm: number): number {
  return Math.max(1, Math.ceil((holderEm(line, family) * fontMm * 1.08) / Math.max(1, wMm * 0.92)));
}
/** The box height of `rows` rows. */
const freeHeightMm = (rows: number, fontMm: number) => rows * fontMm * LINE_HEIGHT + fontMm * 0.35;

export interface FreePlan { box: Box; spot: FreeSpot; textAlign: Align; widthMm: number; overDecor: boolean }

/**
 * The best spot on a model page for a caption of photo `photoId`: `heightMm(w)`
 * says how tall the words are in a box `w` mm wide (null: they need more rows
 * than allowed). Null = no spot in the safe zone off every window and text.
 * `skip`: elements that do not block (this photo's previous caption).
 */
export function findFreeSpot(
  page: CaptionPage, photoId: string, heightMm: (wMm: number) => number | null,
  o: { safe: Box; align?: Align; skip?: (e: PageEl) => boolean; overDecor?: boolean },
): FreePlan | null {
  const els = page.elements ?? [];
  const parts = new Set(windowParts(page, photoId));
  const win = photoWindow(page, photoId);
  if (!win) return null;
  const mine = (e: PageEl) => parts.has(e);
  const hard: Box[] = [pad(win, FREE_GAP_MM * 0.9)];
  const soft: Box[] = [];
  for (const e of els) {
    if (!e || mine(e) || isPaper(e) || o.skip?.(e)) continue;
    if (isDecor(e)) soft.push(core(boundsOf(e)));
    else hard.push(pad(boundsOf(e), FREE_PAD_MM));
  }
  const safe = o.safe;
  const gapX = mmX(FREE_GAP_MM), gapY = mmY(FREE_GAP_MM);
  const winWmm = (win.width / 100) * PAGE_W_MM;
  const safeWmm = (safe.width / 100) * PAGE_W_MM;
  const base = Math.min(safeWmm, FREE_MAX_W_MM, Math.max(FREE_MIN_W_MM, winWmm));
  const widths = [...new Set([base, Math.max(FREE_MIN_W_MM, base * 0.8), Math.max(FREE_MIN_W_MM, base * 0.62)].map((w) => Math.min(w, safeWmm)))];
  const aligns = [...new Set<Align>([o.align ?? 'center', 'center', 'left', 'right'])];
  const clampX = (x: number, w: number) => Math.min(Math.max(x, safe.x), safe.x + safe.width - w);
  const cands: FreePlan[] = [];
  const consider = (box: Box, spot: FreeSpot, textAlign: Align, widthMm: number) => {
    if (!within(box, safe) || hard.some((h) => area(clip(box, h)) > 0)) return;
    cands.push({ box, spot, textAlign, widthMm, overDecor: soft.some((s) => area(clip(box, s)) > 0) });
  };
  for (const far of [1, 2]) {
    for (const spot of ['below', 'above'] as const) {
      for (const wMm of widths) {
        const hMm = heightMm(wMm);
        if (hMm == null) continue;
        const w = mmX(wMm), h = mmY(hMm);
        const y = spot === 'below' ? win.y + win.height + gapY * far : win.y - gapY * far - h;
        for (const a of aligns) {
          const x = a === 'left' ? win.x : a === 'right' ? win.x + win.width - w : win.x + (win.width - w) / 2;
          consider({ x: clampX(x, w), y, width: w, height: h }, spot, a, wMm);
        }
      }
    }
  }
  for (const spot of ['right', 'left'] as const) {
    const room = spot === 'right' ? safe.x + safe.width - (win.x + win.width + gapX) : win.x - gapX - safe.x;
    const wMm = Math.min(FREE_SIDE_MAX_W_MM, (room / 100) * PAGE_W_MM);
    if (wMm < FREE_SIDE_MIN_W_MM) continue;
    const hMm = heightMm(wMm);
    if (hMm == null) continue;
    const w = mmX(wMm), h = mmY(hMm);
    const x = spot === 'right' ? win.x + win.width + gapX : win.x - gapX - w;
    for (const y of [win.y + win.height - h, win.y, win.y + (win.height - h) / 2]) {
      consider({ x, y, width: w, height: h }, spot, spot === 'right' ? 'left' : 'right', wMm);
    }
  }
  // Over a decoration's main body only when asked (after the photo itself was tried).
  return cands.find((c) => !c.overDecor) ?? (o.overDecor ? cands[0] ?? null : null);
}

/** The box, rounded INWARD to the element grid (2 decimals of %), so rounding never leaves the safe zone. */
const inward = (b: Box): Box => {
  const x = Math.ceil(b.x * 100) / 100, y = Math.ceil(b.y * 100) / 100;
  return { x, y, width: Math.floor((b.x + b.width - x) * 100) / 100, height: Math.floor((b.y + b.height - y) * 100) / 100 };
};

/**
 * How many characters each language line may have for a free caption of this
 * photo — the length the vision model is asked to keep to, from the box the
 * placement would choose (FREE_MAX_ROWS rows; one row when two do not fit).
 * Null = no spot on the paper for it.
 */
export function freeCaptionCapacity(
  page: CaptionPage, photoId: string, langs: ReadonlyArray<string>,
  o: { safe: Box; fontSize: number; fontFamily: string; align?: Align; overDecor?: boolean },
): { maxChars: number; spot: FreeSpot } | null {
  const n = Math.max(1, langs.length);
  const fontMm = o.fontSize / 2;
  const k = Math.max(...Array.from({ length: n }, (_, i) => (langs[i] === 'ar' ? Math.max(0.7, fontK(o.fontFamily)) : fontK(o.fontFamily))));
  for (const rows of [FREE_MAX_ROWS, 1]) {
    const plan = findFreeSpot(page, photoId, () => freeHeightMm(rows * n, fontMm), { safe: o.safe, align: o.align, overDecor: o.overDecor });
    if (!plan) continue;
    const perRow = (plan.widthMm * 0.92) / (fontMm * 1.08 * k);
    const maxChars = Math.min(MAX_HOLDER_LINE, Math.floor(rows * perRow * (rows > 1 ? 0.85 : 0.95)));
    if (maxChars >= MIN_HOLDER_CHARS) return { maxChars, spot: plan.spot };
  }
  return null;
}

export type FreePlaceResult =
  | { ok: true; element: CaptionElement; spot: FreeSpot }
  | { ok: false; reason: 'no_room' | 'too_long' | 'not_in_album'; why: string };

/**
 * Place ONE free caption for `photoId` on model page `pages[pi]`, in `look`
 * at `fontSize`. Never throws.
 */
export function placeFreeCaption(
  pages: CaptionPage[], pi: number, photoId: string, text: string,
  o: { fontSize: number; look: FreeLook; themedBook?: boolean; id?: string; skip?: (e: PageEl) => boolean; overDecor?: boolean },
): FreePlaceResult {
  const page = pages[pi];
  const slotIndex = (page?.photos ?? []).indexOf(photoId);
  if (!page || slotIndex < 0) return { ok: false, reason: 'not_in_album', why: 'the photo is not on this page' };
  const lines = lineList(text);
  if (!lines.length) return { ok: false, reason: 'too_long', why: 'there are no words to write' };
  const fontMm = o.fontSize / 2;
  const family = o.look.fontFamily;
  const heightMm = (wMm: number) => {
    const rows = lines.map((l) => freeRows(l, family, fontMm, wMm));
    return rows.some((r) => r > FREE_MAX_ROWS) ? null : freeHeightMm(rows.reduce((a, b) => a + b, 0), fontMm);
  };
  if (heightMm(FREE_MAX_W_MM) == null) {
    const k = fontK(family);
    return { ok: false, reason: 'too_long', why: `the words need more than ${FREE_MAX_ROWS} rows a language next to the photo (≈ ${Math.floor((FREE_MAX_W_MM * 0.92 * FREE_MAX_ROWS) / (fontMm * 1.08 * k))} characters a line) — shorten them` };
  }
  const safe = pageSafeBox(Number(page.page), o.themedBook);
  const plan = findFreeSpot(page, photoId, heightMm, { safe, align: o.look.align, skip: o.skip, overDecor: o.overDecor });
  if (!plan) return { ok: false, reason: 'no_room', why: 'no free place next to this photo inside the print safe zone (12 mm from the edges, 15 mm from the spine)' };
  const box = inward(plan.box);
  return {
    ok: true, spot: plan.spot,
    element: {
      id: o.id ?? `caption-${photoId}`, type: 'text',
      x: box.x, y: box.y, width: box.width, height: box.height,
      text: lines.join('\n'),
      fontFamily: family, fontSize: o.fontSize, fontWeight: o.look.fontWeight, fontStyle: o.look.fontStyle, fontColor: o.look.fontColor,
      textAlign: plan.textAlign, opacity: 1, outlineColor: o.look.fontColor, outlineWidth: 0,
      captionFor: photoId, captionSlot: slotIndex, captionLayout: page.layout, captionFree: true,
    },
  };
}

/**
 * The fallback of a model page: the old caption ON the photo (its top or
 * bottom band, off faces), inside the page's safe zone.
 */
function onPhotoFallback(
  page: CaptionPage, photoId: string, text: string, photo: CaptionPhoto | undefined,
  o: PlaceOpts & { themedBook?: boolean },
): PlaceResult {
  if (!photo || !(photo.width > 0) || !(photo.height > 0)) return { ok: false, reason: 'no_photo_size', why: 'the size of this photo is unknown, so its faces cannot be avoided' };
  const placed = placeCaption(page, photoId, text, photo, { ...o, safe: pageSafeBox(Number(page.page), o.themedBook) });
  if (placed.ok) return placed;
  return placed.reason === 'face' || placed.reason === 'text' || placed.reason === 'too_long'
    ? { ok: false, reason: 'no_room', why: `no free place next to this photo inside the print safe zone, nor on it (${placed.why})` }
    : placed;
}

// ─── THE BAND'S COLOURS: THE COVER'S ─────────────────────────────────────────

const HEX6 = /^#[0-9a-f]{6}$/i;
function relLum(h: string): number {
  const ch = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}
function contrast(a: string, b: string): number {
  const la = relLum(a), lb = relLum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The band of a book: its cover spine plate, with the spine ink when it reads
 * on it (WCAG 4.5:1), else near-black or white. No spine colour → Roaa's pink.
 * (worker/src/lib/readableText.ts bookPalette, the same rule.)
 */
export function bandStyleFrom(coverCustomizations: unknown): CaptionStyle {
  const sc = ((coverCustomizations ?? {}) as { spineConfig?: { bgColor?: unknown; textColor?: unknown } }).spineConfig ?? {};
  const band = String(sc.bgColor ?? '').trim();
  if (!HEX6.test(band)) return { ...BAND_DEFAULT };
  const pref = String(sc.textColor ?? '').trim();
  const B = band.toUpperCase();
  if (HEX6.test(pref) && contrast(pref, B) >= 4.5) return { band: B, ink: pref.toUpperCase() };
  return { band: B, ink: contrast('#FFFFFF', B) >= contrast(INK_DARK, B) ? '#FFFFFF' : INK_DARK };
}
